/**
 * BeHeard's ONE user-initiated sign-out: clear this tab's session, then hand the
 * browser to Central's RP-initiated logout (services/oidc/logout.ts, suite
 * contract 1.1). Every "Sign out" button routes here.
 *
 * Why it exists: two surfaces signed out LOCALLY only —
 *   - WorkspaceClosedOverlay dispatched `centralLogout()` and nothing else, and
 *   - the shell adapter supplied no `onSignOut` at all.
 * A local-only logout leaves Central's SSO session alive, so AuthGuard sends
 * the user to /login, /login re-authorizes, and they are silently signed back in
 * to the very workspace they were trying to leave. That is the bug contract 1.1
 * names ("a local-only logout that immediately re-authorizes is a bug").
 *
 * `useCentralAuth().logout` performs the same three local steps; this is the
 * dispatch-only form for callers that are not inside that hook (an overlay, the
 * module-level shell adapter).
 */

import { centralLogout } from '../../store/slices/centralAuthSlice';
import { api } from '../../store/api';
import { clearOidcSession } from './pkce';
import { signOut } from './logout';
import { CENTRAL_URL, OIDC_CLIENT_ID } from '../../config/centralAuth';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

export interface AppSignOutOptions {
  /** Top-level navigation; defaults to window.location.assign. */
  navigate?: (url: string) => void;
}

export function appSignOut(dispatch: Dispatch, options: AppSignOutOptions = {}): void {
  signOut({
    centralUrl: CENTRAL_URL,
    clientId: OIDC_CLIENT_ID,
    clearLocalSession: () => {
      dispatch(centralLogout());
      dispatch(api.util.resetApiState());
      clearOidcSession();
    },
    navigate: options.navigate ?? ((url) => window.location.assign(url)),
  });
}
